import type { FC } from 'react';
import { Button, Link, Tooltip } from '@highereducation/hen-ui';

export interface RankingsDisclosureProps {
  /**
   * Site brand name interpolated into the advertising-disclosure tooltip copy
   * (e.g. "Psychology.org"). Defaults to "This site" when the shortcode carries
   * no `sitename` attribute — matching the legacy
   * `domainLabelWithExtension || 'This site'` fallback in
   * `se-supersonic-ui` `Tooltip/Disclosure.tsx`.
   */
  siteName?: string;
}

/**
 * RankingsDisclosure
 *
 * Restores the auto-generated rankings disclosure blurb above HEN-rendered
 * rankings lists (EDUN-13115). Ports the legacy `SonicRanking`
 * `RankingDisclosureText` trusted-sources copy + the non-Monarch
 * advertising-disclosure tooltip from `Tooltip/Disclosure.tsx`.
 *
 * Built from hen-ui primitives only (`Link`, `Tooltip`, `Button`) because
 * `packages/features` is a leaf in the dependency graph and may only import
 * from `@highereducation/hen-ui` and `@highereducation/wordpress` — it cannot
 * reach the `SponsoredResultsPopover` composite in `dynamic-experiences` or the
 * `MobiusContext` the legacy `Disclosure` read the domain from. The non-Monarch
 * copy is correct for ACO (ACO is not in the legacy Monarch-exclusion list);
 * the domain is interpolated from `siteName` instead of context.
 *
 * The "Sponsored results" tooltip trigger is a focusable hen-ui `Button`
 * (`textOnlyNeutral` / `flush`) mirroring the `SponsoredResultsPopover` pattern
 * so the advertising disclosure is keyboard-accessible (WCAG 2.1 AA).
 */
export const RankingsDisclosure: FC<RankingsDisclosureProps> = ({
  siteName = 'This site',
}) => {
  const advertisingDisclosureCopy = `${siteName} is an advertising-supported site. Featured or trusted partner programs and all school search, finder, or match results are for schools that compensate us. This compensation does not influence our school rankings, resource guides, or other editorially-independent information published on this site.`;

  return (
    <p
      className='text-b5 leading-b5 text-subtext'
      data-testid='rankings-disclosure'
    >
      We use trusted sources like{' '}
      <Link
        href='https://www.petersons.com/'
        target='_blank'
        rel='noopener noreferrer'
        variant='default'
        underline='always'
        // Link defaults to text-base (16px); match the surrounding body copy (text-b5).
        className='text-b5'
      >
        Peterson&apos;s Data
      </Link>{' '}
      and the National Center for Education Statistics to inform the data for
      these schools.{' '}
      <Tooltip content={advertisingDisclosureCopy}>
        <Button type='button' variant='textOnlyNeutral' size='flush'>
          Sponsored results
        </Button>
      </Tooltip>{' '}
      from our partners appear among these rankings and are indicated as such.
    </p>
  );
};
